import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserIcon, 
  PencilIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  ArrowRightOnRectangleIcon,
  BookmarkIcon,
  PlusIcon,
  ListBulletIcon,
  EyeIcon,
  CheckCircleIcon,
  SparklesIcon,
  CircleStackIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI, imageAPI, savedAPI, billingAPI, paymentsAPI } from '../services/api';
import { purchaseService } from '../services/purchaseService';
import SavedListModal from '../components/SavedListModal';
import { useMobile } from '../hooks/useMobile';
import { getImageUrl } from '../config/runtime';
import { subscriptionPlans } from '../config/subscriptionPlans';
import toast from 'react-hot-toast';

const UserProfile = () => {
  const { user: authUser, logout } = useAuth();
  const { isMobile } = useMobile();
  const { displayMode, setDisplayMode } = useTheme();
  const navigate = useNavigate();
  const [showProfileEditModal, setShowProfileEditModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const [subscriptionCheckoutKey, setSubscriptionCheckoutKey] = useState(null);
  const [isStripePortalLoading, setIsStripePortalLoading] = useState(false);
  const subscriptionsEnabled = false;
  
  // Image style options
  const imageStyleOptions = [
    {
      value: "IKEA-style line drawings with simple black outlines",
      label: "IKEA Style",
      description: "Clean line drawings with simple black outlines",
      preview: "📐"
    },
    {
      value: "photorealistic photography style with natural lighting and realistic details",
      label: "Photorealistic",
      description: "Realistic photography with natural lighting",
      preview: "📷"
    },
    {
      value: "technical illustration style with clean lines and minimal shading",
      label: "Technical Illustration",
      description: "Clean technical drawings with minimal shading",
      preview: "📊"
    },
    {
      value: "realistic watercolor painting style with soft edges",
      label: "Watercolor",
      description: "Soft watercolor paintings with gentle edges",
      preview: "🎨"
    },
    {
      value: "modern flat design with bold colors and geometric shapes",
      label: "Modern Flat Design",
      description: "Bold colors and geometric shapes",
      preview: "🔷"
    },
    {
      value: "botanical illustration style with detailed plant anatomy and scientific accuracy",
      label: "Botanical Illustration",
      description: "Scientific plant drawings with detailed anatomy",
      preview: "🌿"
    },
    {
      value: "field guide sketch style with hand-drawn observations and annotations",
      label: "Field Guide Style",
      description: "Hand-drawn sketches like a naturalist's field notes",
      preview: "📝"
    },
    {
      value: "nature documentary style with cinematic composition and earthy tones",
      label: "Nature Documentary",
      description: "Cinematic nature photography with earthy tones",
      preview: "🎬"
    },
    {
      value: "vintage natural history illustration style with aged paper texture",
      label: "Vintage Natural History",
      description: "Classic scientific illustrations with aged appearance",
      preview: "📜"
    },
    {
      value: "minimalist nature sketch style with clean lines and organic shapes",
      label: "Minimalist Nature",
      description: "Simple, clean sketches emphasizing organic forms",
      preview: "🌱"
    }
  ];

  // Display mode options
  const displayModeOptions = [
    {
      value: "system",
      label: "System Default",
      description: "Follows your device settings",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      value: "light",
      label: "Light Mode",
      description: "Always use light theme",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    },
    {
      value: "dark",
      label: "Dark Mode",
      description: "Always use dark theme",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      )
    }
  ];

  const [activeTab, setActiveTab] = useState('saved'); // Only 'saved' tab remains
  const [isImageStyleDropdownOpen, setIsImageStyleDropdownOpen] = useState(false);
  const imageStyleDropdownRef = useRef(null);
  const queryClient = useQueryClient();

  // Fetch current user profile
  const { data: userResponse, isLoading, error } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => authAPI.getProfile(),
    initialData: authUser ? { data: authUser } : undefined
  });

  const user = userResponse?.data || authUser;

  const { data: billingStatusResponse } = useQuery({
    queryKey: ['billingStatus'],
    queryFn: () => billingAPI.getStatus(),
    enabled: !!authUser
  });
  const billingStatus = billingStatusResponse?.data?.data;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (profileData) => authAPI.updateProfile(profileData),
    onSuccess: (response) => {
      queryClient.setQueryData(['currentUser'], response);
      setShowProfileEditModal(false);
      setProfileForm({});
      toast.success('Profile updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update profile');
      console.error('Profile update error:', error);
    }
  });

  // State for search functionality
  const [searchTerm, setSearchTerm] = useState('');

  // Saved lists functionality
  const [listModal, setListModal] = useState({ isOpen: false, list: null, mode: 'create' });

  // Fetch saved lists with infinite query
  const { 
    data: savedListsData,
    isLoading: isSavedListsLoading,
    isFetchingNextPage: isFetchingSavedNext,
    hasNextPage: hasSavedNext,
    fetchNextPage: fetchSavedNext,
    error: savedListsError,
    refetch: refetchSavedLists
  } = useInfiniteQuery({
    queryKey: ['saved-lists-profile'],
    queryFn: ({ pageParam = 0 }) => savedAPI.getLists({
      offset: pageParam,
      limit: 20,
      include_items: false
    }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.data?.data?.pagination;
      const lastPageLists = lastPage?.data?.data?.lists || [];
      const requestedLimit = 20;
      
      // Stop immediately if we got an empty page
      if (lastPageLists.length === 0) {
        return undefined;
      }
      
      if (lastPageLists.length < requestedLimit) {
        return undefined;
      }
      
      if (pagination && pagination.hasMore === false) {
        return undefined;
      }
      
      // Safety check: stop after 10 pages
      const totalPages = allPages.length;
      if (totalPages >= 10) {
        return undefined;
      }
      
      if (pagination && pagination.hasMore === true && lastPageLists.length === requestedLimit) {
        const totalLists = allPages.reduce((total, page) => total + (page?.data?.data?.lists?.length || 0), 0);
        return totalLists;
      }
      
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!authUser && activeTab === 'saved'
  });

  // Flatten all pages into a single lists array
  const savedLists = savedListsData?.pages.reduce((acc, page) => {
    return [...acc, ...(page?.data?.data?.lists || [])];
  }, []) || [];

  // Intersection observer ref for infinite scroll
  const observerTarget = useRef(null);

  // Intersection Observer for infinite scroll (only for saved lists now)
  const observeLastProject = useCallback(
    (node) => {
      if (isSavedListsLoading || isFetchingSavedNext || !hasSavedNext) return;
      if (observerTarget.current) observerTarget.current.disconnect();
      
      observerTarget.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasSavedNext && !isFetchingSavedNext) {
          fetchSavedNext();
        }
      }, {
        threshold: 0.1,
      });
      
      if (node) observerTarget.current.observe(node);
    },
    [isSavedListsLoading, isFetchingSavedNext, hasSavedNext, fetchSavedNext]
  );



  const handleEditProfile = () => {
    setProfileForm({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      avatar: user?.avatar || '',
      interests: user?.interests || '',
      app_settings: user?.app_settings || {}
    });
    setShowProfileEditModal(true);
  };

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login', { replace: true });
      // toast.success('See you soon!');
    } catch (error) {
      toast.error('Failed to sign out');
      console.error('Sign out error:', error);
    }
  };

  const refreshBillingStatusSoon = useCallback(() => {
    window.setTimeout(() => {
      queryClient.invalidateQueries({ queryKey: ['billingStatus'] });
    }, 1500);
  }, [queryClient]);

  const handleTokenPackPurchase = async () => {
    try {
      const result = await purchaseService.purchaseTokens({
        packId: 'topup_100',
        userId: user?.id,
      });

      if (result?.channel === 'revenuecat') {
        toast.success('Purchase completed. Your token balance will refresh shortly.');
        refreshBillingStatusSoon();
      }
    } catch (error) {
      if (error?.userCancelled) {
        return;
      }

      console.error('Token purchase failed:', error);
      toast.error(error?.message || 'Failed to start checkout.');
    }
  };

  const handleManageBilling = async () => {
    if (isStripePortalLoading) return;

    const ok = window.confirm(
      'This will open Stripe where you can manage your subscription. Continue?'
    );
    if (!ok) return;

    setIsStripePortalLoading(true);
    try {
      await purchaseService.openManageBilling({
        userId: user?.id,
        returnUrl: window.location.href,
      });
    } catch (error) {
      console.error('Billing portal failed:', error);
      toast.error('Failed to open billing portal.');
    } finally {
      setIsStripePortalLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmInput !== user?.username) return;
    setIsDeletingAccount(true);
    try {
      await authAPI.deleteAccount();
      await logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account. Please try again.');
      console.error('Account deletion error:', error);
      setIsDeletingAccount(false);
    }
  };

  const handleProfileFormSubmit = (e) => {
    e.preventDefault();
    updateProfileMutation.mutate(profileForm);
  };

  const handleProfileInputChange = (field, value) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAppSettingsChange = (key, value) => {
    setProfileForm(prev => ({
      ...prev,
      app_settings: {
        ...prev.app_settings,
        [key]: value
      }
    }));
  };

  const getCurrentImageStyleOption = () => {
    const currentStyle = profileForm.app_settings?.image_style || 'IKEA-style line drawings with simple black outlines';
    return imageStyleOptions.find(option => option.value === currentStyle) || imageStyleOptions[0];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imageStyleDropdownRef.current && !imageStyleDropdownRef.current.contains(event.target)) {
        setIsImageStyleDropdownOpen(false);
      }
    };

    if (isImageStyleDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isImageStyleDropdownOpen]);

  // Saved lists handlers
  const handleCreateList = () => {
    setListModal({ isOpen: true, list: null, mode: 'create' });
  };

  const closeListModal = () => {
    setListModal({ isOpen: false, list: null, mode: 'create' });
  };

  const handleViewList = (list) => {
    navigate(`/saved/${list.id}`, {
      state: {
        previousPath: '/profile',
        transitionDirection: 'forward'
      }
    });
  };

  const handleAvatarFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      setIsUploadingAvatar(true);
      const fd = new FormData();
      fd.append('image', file);
      const res = await imageAPI.upload(fd);
      const imageUrl = res?.data?.data?.url;
      if (imageUrl) {
        setProfileForm(prev => ({ ...prev, avatar: imageUrl }));
        toast.success('Image uploaded');
      } else {
        toast.error('Upload failed');
      }
    } catch (err) {
      console.error('Avatar upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setIsUploadingAvatar(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const getDisplayName = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user?.firstName) {
      return user.firstName;
    } else if (user?.username) {
      return user.username;
    }
    return 'User';
  };

  const getInitials = (user) => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    } else if (user?.firstName) {
      return user.firstName[0].toUpperCase();
    } else if (user?.username) {
      return user.username[0].toUpperCase();
    }
    return 'U';
  };




  const closeModals = () => {
    setShowProfileEditModal(false);
    setProfileForm({});
  };

  // Animation variants for bottom slide (mobile) and fade/scale (desktop)
  const modalVariants = {
    hidden: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
    },
    exit: isMobile ? {
      y: '100%',
      opacity: 0,
    } : {
      y: 20,
      opacity: 0,
      scale: 0.95,
    },
  };

  // Background variants for fade effect
  const backgroundVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  // Animation transition settings  
  const transition = {
    type: 'tween',
    ease: [0.4, 0, 0.2, 1],
    duration: isMobile ? 0.35 : 0.25,
  };

  const backgroundTransition = {
    type: 'tween',
    ease: 'easeOut',
    duration: 0.2,
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-8"></div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow h-96"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="text-center">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h2 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">Profile Error</h2>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                Unable to load your profile. Please try again.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 pt-0">
        <div className="px-4 pt-6 pb-24 md:pb-6 sm:px-0">

        {/* Profile Header */}
        <div className="pb-3 sm:pb-6">
          {/* User Info Section */}
          <div className="flex flex-col sm:flex-row sm:items-start gap-4 mb-4 sm:mb-6">
            {/* Avatar and Basic Info */}
            <div className="flex items-center gap-4">
              <div className="h-20 w-20 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                {user?.avatar ? (
                  <img src={getImageUrl(user.avatar)} alt="" className="h-20 w-20 rounded-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-2xl">{getInitials(user)}</span>
                )}
              </div>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{getDisplayName(user)}</h1>
                <p className="text-lg text-gray-600 dark:text-gray-300">@{user?.username}</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Tokens: {billingStatus?.tokenBalance ?? user?.tokenBalance ?? 0}
                </p>
                {user?.createdAt && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Member since {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 sm:gap-2 sm:ml-auto overflow-x-auto scrollbar-hide -mx-1 px-1">
              <Link
                to={`/users/${user?.username}`}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600/20 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors rounded-2xl text-sm whitespace-nowrap"
              >
                <EyeIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">View Profile</span>
                <span className="sm:hidden">Public</span>
              </Link>
              <button
                onClick={() => setShowSubscriptionModal(true)}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600/20 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors rounded-2xl text-sm whitespace-nowrap"
              >
                <CircleStackIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Manage tokens</span>
                <span className="sm:hidden">Tokens</span>
              </button>
              <button
                onClick={handleEditProfile}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600/20 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors rounded-2xl text-sm whitespace-nowrap"
              >
                <PencilIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Edit Profile</span>
                <span className="sm:hidden">Edit</span>
              </button>
              <button
                onClick={handleSignOut}
                className="inline-flex gap-2 items-center px-3 sm:px-4 py-2 border border-gray-300 dark:border-gray-600/20 shadow-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 hover:border-red-300 dark:hover:border-red-600 hover:text-red-600 dark:hover:text-red-400 transition-colors rounded-2xl text-sm whitespace-nowrap"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Sign Out</span>
                <span className="sm:hidden">Sign Out</span>
              </button>
            </div>
          </div>

          {/* User Stats */}
          {/* <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{savedLists.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Saved Lists</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{userCompletedProjects.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{userCreatedProjects.length}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Created</div>
            </div>
          </div> */}
          
          {/* Description - Full Width */}
          {/* {user?.interests && (
            <div className="mt-4 max-w-3xl">
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">
                {user.interests}
              </p>
            </div>
          )} */}
        </div>

        {/* Tabs and Projects Section */}
        <div className="mt-0 sm:mt-2">

          {/* Tabs Header */}
          <div className="flex items-center gap-4 sm:gap-8 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab('saved')}
              className={`pb-2 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                activeTab === 'saved'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <span className="hidden sm:inline">Saved Projects</span>
              <span className="sm:hidden">Saved</span>
            </button>
            
          </div>

          {/* Search */}
          {activeTab !== 'saved' && (
            <div className="my-6">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-600/20 rounded-lg leading-5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            </div>
          )}

          {/* Content */}
          {(
            /* Saved Lists */
            <>
              {isSavedListsLoading && savedLists.length === 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 animate-pulse">
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full mb-4"></div>
                      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                    </div>
                  ))}
                </div>
              ) : savedListsError ? (
                <div className="text-center py-12">
                  <div className="text-red-500 dark:text-red-400 mb-4">
                    Error loading saved lists
                  </div>
                  <button 
                    onClick={() => refetchSavedLists()}
                    className="btn-primary"
                  >
                    Retry
                  </button>
                </div>
              ) : savedLists.length > 0 ? (
                <>
                  {/* Create New List Button */}
                  <div className="border-b border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleCreateList}
                      className="w-full flex items-center gap-4 p-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-left"
                    >
                      <div className="w-12 h-12 bg-gray-700 dark:bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                        <PlusIcon className="w-6 h-6 text-gray-50 dark:text-gray-600" />
                      </div>
                      <div>
                        <div className="text-lg font-medium text-gray-900 dark:text-white">
                          Create new list
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Lists */}
                  <div className="space-y-0">
                    {savedLists.map((list, index) => {
                      const isNearEnd = index >= savedLists.length - 3;
                      
                      return (
                        <div
                          key={list.id}
                          ref={isNearEnd ? observeLastProject : null}
                          className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-shadow duration-200"
                        >
                          <div className="flex items-center justify-between p-4">
                            <button
                              onClick={() => handleViewList(list)}
                              className="flex items-center gap-4 flex-1 text-left min-w-0 overflow-hidden"
                            >
                              <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-xl flex items-center justify-center flex-shrink-0">
                                <BookmarkIcon className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                              </div>
                              <div className="flex-1 min-w-0 overflow-hidden">
                                <div className="flex items-center gap-2 mb-1 min-w-0">
                                  <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate flex-1 min-w-0" title={list.name}>
                                    {list.name}
                                  </h3>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {list.isDefault && (
                                      <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded whitespace-nowrap">
                                        Default
                                      </span>
                                    )}
                                    {!list.isPublic && (
                                      <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 min-w-0">
                                  <span className="flex-shrink-0">{list.itemCount || 0} item{(list.itemCount || 0) !== 1 ? 's' : ''}</span>
                                  {list.description && (
                                    <span className="truncate flex-1 min-w-0" title={list.description}>{list.description}</span>
                                  )}
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Infinite scroll loading spinner */}
                  {isFetchingSavedNext && (
                    <div className="flex justify-center items-center py-8">
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                        <span className="text-gray-600 dark:text-gray-400 text-sm">Loading more lists...</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <div className="mx-auto w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
                    <ListBulletIcon className="h-12 w-12 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No saved lists yet
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-8 max-w-sm mx-auto">
                    Create your first list to organize and save your favorite projects for easy access.
                  </p>
                  <button
                    onClick={handleCreateList}
                    className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-sm font-medium rounded-2xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Your First List
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Profile Edit Modal */}
        <AnimatePresence>
          {showProfileEditModal && (
            <motion.div 
              className={`fixed inset-0 bg-black bg-opacity-50 z-[60000] ${
                isMobile 
                  ? 'flex items-stretch justify-center' 
                  : 'flex items-center justify-center p-4'
              }`}
              variants={backgroundVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={backgroundTransition}
              onClick={closeModals}
              style={{ isolation: 'isolate', contain: 'layout style paint' }}
            >
            <motion.div 
              className={`bg-white dark:bg-gray-800 w-full flex flex-col overflow-hidden relative z-[60001] ${
                isMobile 
                  ? 'rounded-none' 
                  : 'max-w-2xl max-h-[90vh] rounded-lg shadow-xl'
              }`}
              style={isMobile ? { minHeight: '100dvh', height: '100dvh', maxHeight: '100dvh', width: '100vw' } : {}}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={transition}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className={`flex items-center justify-between border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${
                  isMobile ? 'px-4 py-4 pt-safe-top' : 'p-6'
                }`} style={isMobile ? { paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' } : {}}>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Edit Profile</h2>
                  <button
                    onClick={closeModals}
                    className={`text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 ${
                      isMobile ? 'p-2 -mr-2' : ''
                    }`}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {/* Form */}
                <form onSubmit={handleProfileFormSubmit} className="flex flex-col flex-1 overflow-hidden">
                  <div className={`flex-1 overflow-y-auto ${
                    isMobile ? 'px-4 py-4' : 'p-6'
                  }`} style={{ 
                    overscrollBehavior: 'contain', 
                    paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 100px)' : 'calc(env(safe-area-inset-bottom, 0px) + 88px)' 
                  }}>
              {/* Avatar uploader */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Profile Photo
                </label>
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full overflow-hidden flex items-center justify-center">
                    {profileForm.avatar ? (
                      <img src={getImageUrl(profileForm.avatar)} alt="avatar" className="h-16 w-16 object-cover" />
                    ) : user?.avatar ? (
                      <img src={getImageUrl(user.avatar)} alt="avatar" className="h-16 w-16 object-cover" />
                    ) : (
                      <span className="text-white font-bold text-xl">{getInitials(user)}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => avatarInputRef.current?.click()}
                      disabled={isUploadingAvatar}
                      className="px-3 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-2xl disabled:opacity-50"
                    >
                      {isUploadingAvatar ? 'Uploading…' : 'Upload Image'}
                    </button>
                    {profileForm.avatar && (
                      <button
                        type="button"
                        onClick={() => setProfileForm(prev => ({ ...prev, avatar: '' }))}
                        className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-2xl"
                      >
                        Remove
                      </button>
                    )}
                    <input
                      ref={avatarInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAvatarFileChange}
                      className="hidden"
                    />
                  </div>
                </div>
                {profileForm.avatar && (
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">This image will be used as your profile photo.</p>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* First Name */}
                    <div>
                      <label htmlFor="profile-firstName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        id="profile-firstName"
                        value={profileForm.firstName || ''}
                        onChange={(e) => handleProfileInputChange('firstName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600/20 rounded-2xl shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter your first name"
                      />
                    </div>

                    {/* Last Name */}
                    <div>
                      <label htmlFor="profile-lastName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        id="profile-lastName"
                        value={profileForm.lastName || ''}
                        onChange={(e) => handleProfileInputChange('lastName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600/20 rounded-2xl shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter your last name"
                      />
                    </div>

                    {/* Email (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <div className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600/20 rounded-2xl text-gray-500 dark:text-gray-400">
                        {user?.email}
                      </div>
                    </div>

                    {/* Username (read-only) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Username
                      </label>
                      <div className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600/20 rounded-2xl text-gray-500 dark:text-gray-400">
                        @{user?.username}
                      </div>
                    </div>
                  </div>

              {/* Interests */}
              <div className="mt-6">
                <label htmlFor="profile-interests" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Interests
                </label>
                <textarea
                  id="profile-interests"
                  rows={4}
                  value={profileForm.interests || ''}
                  onChange={(e) => handleProfileInputChange('interests', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600/20 rounded-2xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Share your nature and outdoor interests..."
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Optional. This appears on your public profile.</p>
              </div>

              {/* Display Mode Preference */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Display Mode
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Choose how the app appears on your device.
                </p>
                
                {/* Radio Button Group */}
                <div className="space-y-2">
                  {displayModeOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${
                        displayMode === option.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-300 dark:border-gray-600/20 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="displayMode"
                        value={option.value}
                        checked={displayMode === option.value}
                        onChange={(e) => setDisplayMode(e.target.value)}
                        className="sr-only"
                      />
                      <div className={`flex-shrink-0 ${
                        displayMode === option.value
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {option.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${
                          displayMode === option.value
                            ? 'text-blue-900 dark:text-blue-100'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {option.description}
                        </div>
                      </div>
                      {displayMode === option.value && (
                        <svg className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Image Style Preference */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  AI Image Style Preference
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Choose the visual style for AI-generated instruction images in your projects.
                </p>
                
                {/* Custom Dropdown */}
                <div className="relative" ref={imageStyleDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setIsImageStyleDropdownOpen(!isImageStyleDropdownOpen)}
                    className="w-full flex items-center gap-3 px-3 py-2 border border-gray-300 dark:border-gray-600/20 rounded-2xl bg-white dark:bg-gray-700 text-left focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <span className="text-lg">{getCurrentImageStyleOption().preview}</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {getCurrentImageStyleOption().label}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {getCurrentImageStyleOption().description}
                      </div>
                    </div>
                    <svg className={`h-5 w-5 text-gray-400 transition-transform ${isImageStyleDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {isImageStyleDropdownOpen && (
                    <div className="absolute z-10 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600/20 rounded-2xl shadow-lg max-h-60 overflow-auto">
                      {imageStyleOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            handleAppSettingsChange('image_style', option.value);
                            setIsImageStyleDropdownOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-600 focus:outline-none focus:bg-gray-100 dark:focus:bg-gray-600"
                        >
                          <span className="text-lg">{option.preview}</span>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {option.label}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {option.description}
                            </div>
                          </div>
                          {(profileForm.app_settings?.image_style || 'IKEA-style line drawings with simple black outlines') === option.value && (
                            <svg className="h-4 w-4 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="mt-8 pt-6 border-t border-red-100 dark:border-red-900/30">
                <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50/60 dark:bg-red-900/10 p-4 sm:p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-sm font-semibold text-red-700 dark:text-red-400">Delete account</h3>
                      <p className="mt-1 text-sm text-red-600/80 dark:text-red-300/80 max-w-xl">
                        Permanently remove your account, chats, uploads, saved lists, and all associated data.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setDeleteConfirmInput('');
                        setShowDeleteAccountModal(true);
                      }}
                      className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-2xl border border-red-300 dark:border-red-800 bg-white dark:bg-gray-800 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                    >
                      <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>

                  </div>

                  {/* Footer */}
                  <div className={`flex items-center justify-end gap-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${
                    isMobile ? 'fixed inset-x-0 bottom-0 z-[60002] px-4 py-4' : 'p-6'
                  }`} style={isMobile ? { 
                    paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)', 
                    boxShadow: '0 -8px 16px rgba(0,0,0,0.06)' 
                  } : {}}>
                    <button
                      type="button"
                      onClick={closeModals}
                      className={`font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600/20 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        isMobile ? 'px-6 py-3 text-base' : 'px-4 py-2 text-sm'
                      }`}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateProfileMutation.isPending}
                      className={`font-medium text-white bg-blue-600 border border-transparent rounded-2xl hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed ${
                        isMobile ? 'px-6 py-3 text-base' : 'px-4 py-2 text-sm'
                      }`}
                    >
                      {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Subscription Modal */}
        <AnimatePresence>
          {showSubscriptionModal && (() => {
            // Determine if user has an active subscription
            const isSubscriptionActive = billingStatus?.status === 'active';
            const currentPlanKey = isSubscriptionActive ? billingStatus?.planKey : null;
            
            // Find the current plan details
            const getCurrentPlan = () => {
              if (!isSubscriptionActive) return null;
              return subscriptionPlans.find(p => 
                p.priceKey === `plan_${currentPlanKey}` || 
                p.priceKey === currentPlanKey ||
                p.id === currentPlanKey
              ) || null;
            };
            
            const currentPlan = getCurrentPlan();
            const currentPlanLabel = currentPlan?.name || 'Free';
            const tokenBalance = billingStatus?.tokenBalance ?? user?.tokenBalance ?? 0;
            const usesNativeAndroidBilling = purchaseService.isNativeAndroidPurchase({
              userId: user?.id,
            });
            
            // Calculate value metrics for each plan
            const getTokensPerMonth = (plan) => {
              const match = plan.tokensLabel?.match(/(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            };
            
            const getPriceNumber = (plan) => {
              const match = plan.priceLabel?.match(/\$(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            };
            
            const getCostPerToken = (plan) => {
              const tokens = getTokensPerMonth(plan);
              const price = getPriceNumber(plan);
              if (!tokens || !price) return null;
              return (price / tokens).toFixed(2);
            };
            
            // Find the recommended plan (middle tier for upsell)
            const recommendedPlanId = 'canopy';
            
            return (
            <motion.div
              className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-[60000] ${
                isMobile 
                  ? 'flex items-stretch' 
                  : 'flex items-center justify-center p-4'
              }`}
              variants={backgroundVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={backgroundTransition}
              onClick={() => setShowSubscriptionModal(false)}
            >
              <motion.div
                className={`relative w-full flex flex-col overflow-hidden ${
                  isMobile 
                    ? 'bg-[#0a0f14] rounded-none' 
                    : 'bg-[#0a0f14] max-w-3xl max-h-[90vh] rounded-2xl shadow-2xl border border-white/20'
                }`}
                style={isMobile ? { minHeight: '100dvh', height: '100dvh' } : {}}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={transition}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Ambient gradient background */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                  <div className="absolute -top-24 -right-24 w-96 h-96 bg-emerald-500/8 rounded-full blur-3xl" />
                  <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-cyan-500/6 rounded-full blur-3xl" />
                </div>
                
                {/* Header - Minimal & Functional */}
                <div className={`relative flex items-center justify-between border-b border-white/5 flex-shrink-0 ${
                  isMobile ? 'px-5 py-4 pt-safe-top' : 'px-6 py-5'
                }`} style={isMobile ? { paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' } : {}}>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
                      <SparklesIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-white tracking-tight">
                        Credits & Usage
                      </h2>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowSubscriptionModal(false)}
                    className="p-2 -mr-2 rounded-lg text-gray-500 hover:text-white hover:bg-white/5 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className={`relative flex-1 overflow-y-auto ${
                  isMobile ? 'px-5 py-6' : 'px-6 py-6'
                }`} style={{ 
                  overscrollBehavior: 'contain',
                  paddingBottom: isMobile ? 'calc(env(safe-area-inset-bottom) + 2rem)' : '2rem'
                }}>
                  
                  {/* Balance bar */}
                  <div className="mb-6 flex items-center justify-between gap-4 p-4 rounded-xl bg-white/[0.03] border border-emerald-500/30">
                    <div>
                      <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5">Your balance</div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-3xl font-bold text-white tabular-nums">{tokenBalance.toLocaleString()}</span>
                        <span className="text-sm text-gray-500">tokens</span>
                      </div>

                    </div>
                    {subscriptionsEnabled && isSubscriptionActive && (
                      <button
                        type="button"
                        disabled={isStripePortalLoading}
                        onClick={handleManageBilling}
                        className="shrink-0 px-3 py-1.5 text-xs font-medium text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors disabled:opacity-50"
                      >
                        {isStripePortalLoading ? 'Opening...' : 'Manage billing'}
                      </button>
                    )}
                  </div>

                  {/* Hero: Token top-up */}
                  <div className="rounded-2xl bg-gradient-to-br from-emerald-500/15 to-teal-500/5 border border-emerald-500/30 p-5 mb-4">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-base font-semibold text-white">Top up your credits</span>
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase rounded-full tracking-wide">No subscription</span>
                        </div>
                        <p className="text-sm text-gray-400">100 tokens · one-time · instant</p>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-2xl font-bold text-white">$5</div>
                        <div className="text-xs text-gray-500">$0.05 / token</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={handleTokenPackPurchase}
                      className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-400 px-4 py-3 text-sm font-semibold text-white transition-colors shadow-lg shadow-emerald-500/20"
                    >
                      Get 100 tokens · $5
                    </button>
                  </div>

                  {/* Plans Section */}
                  {subscriptionsEnabled && (
                    <div className="space-y-3 mt-6">
                      <div className="flex items-baseline justify-between mb-3">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-widest">Monthly plans</h3>
                        <span className="text-xs text-gray-600">Cancel anytime</span>
                      </div>
                      
                      {subscriptionPlans.map((plan, index) => {
                        const isPlanActive = isSubscriptionActive && (
                          plan.priceKey === `plan_${currentPlanKey}` || 
                          plan.priceKey === currentPlanKey ||
                          plan.id === currentPlanKey
                        );
                        const isRecommended = plan.id === recommendedPlanId && !isSubscriptionActive;
                        const tokensPerMonth = getTokensPerMonth(plan);
                        const priceNumber = getPriceNumber(plan);
                        const costPerToken = getCostPerToken(plan);
                        
                        return (
                          <motion.div
                            key={plan.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className={`relative group rounded-xl border transition-all duration-200 ${
                              isPlanActive 
                                ? 'bg-emerald-500/8 border-emerald-500/30 ring-1 ring-emerald-500/20' 
                                : isRecommended
                                  ? 'bg-emerald-500/5 border-emerald-500/20 hover:border-emerald-400/40'
                                  : 'bg-white/[0.02] border-white/5 hover:border-white/10 hover:bg-white/[0.04]'
                            }`}
                          >
                            {isRecommended && (
                              <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 bg-emerald-500 rounded-full">
                                <span className="text-[10px] font-bold text-white uppercase tracking-wider">Best Value</span>
                              </div>
                            )}
                            {isPlanActive && (
                              <div className="absolute -top-2.5 left-4 px-2.5 py-0.5 bg-emerald-500 rounded-full flex items-center gap-1">
                                <CheckCircleIcon className="h-3 w-3 text-white" />
                                <span className="text-[10px] font-bold text-white uppercase tracking-wider">Current</span>
                              </div>
                            )}
                            
                            <div className={`p-4 sm:p-5 ${isPlanActive || isRecommended ? 'pt-6' : ''}`}>
                              <div className="flex items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-2 mb-0.5">
                                    <h4 className="text-base font-semibold text-white">{plan.name}</h4>
                                    {costPerToken && (
                                      <span className="text-xs text-gray-500 tabular-nums">${costPerToken}/token</span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">{tokensPerMonth.toLocaleString()} tokens/mo</p>
                                </div>
                                <div className="flex items-baseline gap-0.5 shrink-0">
                                  <span className="text-xl font-bold text-white tabular-nums">${priceNumber}</span>
                                  <span className="text-xs text-gray-500">/mo</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (isPlanActive) return;
                                  setSubscriptionCheckoutKey(plan.priceKey);
                                  try {
                                    const res = await paymentsAPI.createStripeCheckout({ mode: 'subscription', priceKey: plan.priceKey });
                                    const url = res?.data?.data?.url;
                                    if (url) { window.location.assign(url); }
                                    else { toast.error('Unable to start checkout.'); }
                                  } catch (error) {
                                    console.error(error);
                                    toast.error('Failed to start checkout.');
                                  } finally {
                                    setSubscriptionCheckoutKey(null);
                                  }
                                }}
                                disabled={subscriptionCheckoutKey === plan.priceKey || isPlanActive}
                                className={`mt-3 w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-all ${
                                  isPlanActive
                                    ? 'bg-emerald-500/20 text-emerald-400 cursor-default border border-emerald-500/30'
                                    : subscriptionCheckoutKey === plan.priceKey
                                      ? 'bg-white/10 text-white cursor-wait'
                                      : 'bg-white/10 text-white hover:bg-white/15'
                                }`}
                              >
                                {isPlanActive 
                                  ? 'Your Current Plan' 
                                  : subscriptionCheckoutKey === plan.priceKey 
                                    ? 'Redirecting to checkout…' 
                                    : isSubscriptionActive 
                                      ? `Switch to ${plan.name}`
                                      : plan.ctaLabel || `Get ${plan.name}`}
                              </button>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Fine print */}
                  <p className="mt-6 text-center text-xs text-gray-600">
                    {purchaseService.isNativeAndroidPurchase({ userId: user?.id })
                      ? 'Tokens are purchased through Google Play in the Android app.'
                      : 'Secure checkout via Stripe · No hidden fees'}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          );
          })()}
        </AnimatePresence>

        {/* Delete Account Confirmation Modal */}
        <AnimatePresence>
          {showDeleteAccountModal && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-60 z-[70000] flex items-center justify-center p-4"
              variants={backgroundVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={backgroundTransition}
              onClick={() => !isDeletingAccount && setShowDeleteAccountModal(false)}
            >
              <motion.div
                className="bg-white dark:bg-gray-800 w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={transition}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-red-50 dark:bg-red-900/20">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-800/40 flex items-center justify-center">
                      <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h2 className="text-base font-semibold text-red-700 dark:text-red-400">Delete Account</h2>
                  </div>
                  {!isDeletingAccount && (
                    <button
                      onClick={() => setShowDeleteAccountModal(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {/* Body */}
                <div className="px-6 py-5 space-y-4">
                  <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-sm text-red-700 dark:text-red-300 space-y-1">
                    <p className="font-semibold">This action is permanent and cannot be undone.</p>
                    <p>The following will be deleted immediately:</p>
                    <ul className="list-disc pl-4 space-y-0.5 mt-1 text-red-600 dark:text-red-400">
                      <li>Your account and profile</li>
                      <li>All your projects, chats and messages</li>
                      <li>All uploaded images and files</li>
                      <li>Your saved lists</li>
                    </ul>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Type your username <span className="font-mono text-gray-900 dark:text-white">@{user?.username}</span> to confirm
                    </label>
                    <input
                      type="text"
                      value={deleteConfirmInput}
                      onChange={(e) => setDeleteConfirmInput(e.target.value)}
                      placeholder={user?.username}
                      disabled={isDeletingAccount}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600/40 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50"
                      autoComplete="off"
                      autoCapitalize="none"
                    />
                  </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 px-6 pb-5">
                  <button
                    type="button"
                    onClick={() => setShowDeleteAccountModal(false)}
                    disabled={isDeletingAccount}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600/20 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleDeleteAccount}
                    disabled={deleteConfirmInput !== user?.username || isDeletingAccount}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {isDeletingAccount ? 'Deleting...' : 'Delete my account'}
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Saved List Modal */}
        <SavedListModal
          isOpen={listModal.isOpen}
          onClose={closeListModal}
          list={listModal.list}
          mode={listModal.mode}
        />

        </div>
      </div>
    </div>
  );
};

export default UserProfile; 
